package com.eladfinish.bible.contest.ui.components.toolbar

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.eladfinish.bible.contest.ui.components.toolbar.host.AnyToolbarConfiguration

/**
 * The toolbar configuration a screen set, plus the updates its screen registered on top of it.
 *
 * The updates are kept and re-applied, so a screen that sets its configuration again (on web it
 * does whenever a toolbar icon finishes loading) does not lose them. An update applies only to a
 * configuration set by the same [owner] (the screen's back stack entry), so the screen that is
 * leaving does not change the toolbar of the screen that is opening.
 */
internal class ToolbarConfigurationHolder {

    private class OwnedConfiguration(
        val owner: Any?,
        val configuration: AnyToolbarConfiguration,
    )

    private class OwnedUpdate(
        val owner: Any?,
        val update: AnyToolbarConfiguration.() -> AnyToolbarConfiguration,
    )

    private var base: OwnedConfiguration? by mutableStateOf(null)
    private val updates = mutableStateListOf<OwnedUpdate>()

    val configuration: AnyToolbarConfiguration?
        get() {
            val base = base ?: return null
            return updates
                .filter { it.owner == base.owner }
                .fold(base.configuration) { configuration, owned -> owned.update(configuration) }
        }

    fun set(configuration: AnyToolbarConfiguration?, owner: Any?) {
        base = configuration?.let { OwnedConfiguration(owner, it) }
    }

    fun update(update: AnyToolbarConfiguration.() -> AnyToolbarConfiguration) {
        base = base?.let { OwnedConfiguration(it.owner, it.configuration.update()) }
    }

    /** Returns the function that removes the update again. */
    fun addUpdate(
        owner: Any?,
        update: AnyToolbarConfiguration.() -> AnyToolbarConfiguration,
    ): () -> Unit {
        val owned = OwnedUpdate(owner, update)
        updates += owned
        return { updates.remove(owned) }
    }
}
